import PlayIconRect from '@src/svgs/PlayIconRect';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { useFrameLoader } from '../../../hooks/useFrameLoader';
import { useLocalFramePreviewVideoUrl } from '../../../hooks/useLocalPreviewVideoUrl';
import { extractLocalFrameThumbnails } from '../../../services/LocalFrameThumbnails';
import { useVideoCreatorStore } from '../../../stores-v2/VideoCreatorStoreContext';
import { isCompressedRenditionPending } from '../../../utility/storyReadiness';
import SimpleLoadingSpinner from '../../SimpleLoadingSpinner';

import type { Story } from '../../../types.ts/story';

interface TimelineFrameThumbnailsProps {
  storyId: string;
  videoId: string;
  trimStart: number; // Start time in seconds
  duration: number; // Duration in seconds
  elementWidth: number; // Width in pixels on timeline
  height: number; // Thumbnail height
  videoWidth?: number; // Original video width
  videoHeight?: number; // Original video height
}

const Container = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const FrameImage = styled.img<{ isLoading: boolean }>`
  height: 100%;
  object-fit: cover;
  opacity: ${props => (props.isLoading ? 0.5 : 1)};
  transition: opacity var(--transition-normal) ease-in-out;
  will-change: opacity;
`;

const PlaceholderFrame = styled.div`
  background-color: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const LoadingMessage = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: normal;
`;

// Global cache for frame URLs, keyed by video
const globalFrameCache = new Map<string, Map<number, string>>();

// Track videos that don't have frame files (404 errors)
const videosWithoutFrames = new Set<string>();

// Track videos with images that are still processing (403 errors)
const videosWithProcessingImages = new Set<string>();

// Helper to get cache key for a specific video
const getVideoCacheKey = (storyId: string, videoId: string) =>
  `${storyId}:${videoId}`;

// Helper to determine thumbnail loading state based on story data
const getThumbnailLoadingState = (
  story: Story | undefined,
  videoCacheKey: string,
  hasAttemptedLoad: boolean,
  isCurrentlyLoading: boolean,
): ThumbnailLoadingState => {
  // Scenario 1: Pre-AWS video (no thumbnails available)
  if (story && !story.useAws) {
    return {
      state: 'unavailable',
      message: 'Thumbnails not available for this video',
    };
  }

  // Scenario 2: Previously failed to load (404/500 errors)
  if (videosWithoutFrames.has(videoCacheKey)) {
    return {
      state: 'failed',
      message: 'Unable to load thumbnails',
    };
  }

  // Scenario 3: Images are still being processed (403 errors from CloudFront)
  if (videosWithProcessingImages.has(videoCacheKey)) {
    return {
      state: 'processing',
      message:
        "We're still loading your thumbnails... but feel free to get started.",
    };
  }

  // Scenario 4: AWS video that's currently loading for the first time
  // This could indicate thumbnails are still being processed
  if (story?.useAws && isCurrentlyLoading && !hasAttemptedLoad) {
    return {
      state: 'processing',
      message:
        "We're still loading your thumbnails... but feel free to get started.",
    };
  }

  // For other AWS videos, assume they're available
  return { state: 'available' };
};

// TEMPORARY: Feature flag to disable frame loading for performance testing
const DISABLE_FRAME_LOADING = false; // Set to false to enable frame loading

// Dynamic frame calculation constants
const TARGET_FRAME_WIDTH = 60; // Optimal frame width in pixels for good visual density
const MIN_FRAME_WIDTH = 30; // Minimum frame width for readability
const MAX_FRAME_WIDTH = 120; // Maximum frame width before it becomes too sparse
const ABSOLUTE_MAX_FRAMES = 200; // Hard limit to prevent performance issues
const FALLBACK_THRESHOLD = 300; // Fallback to legacy if calculated frames exceed this
const SERVER_FRAME_RETRY_MS = 5000;

interface TimelineFrameThumbnailsPropsWithFallback extends TimelineFrameThumbnailsProps {
  fallbackThumbnailUrl?: string; // URL for legacy single thumbnail
}

// Thumbnail loading states
type ThumbnailLoadingState =
  | { state: 'available' }
  | { state: 'processing'; message: string }
  | { state: 'unavailable'; message: string }
  | { state: 'failed'; message: string };

export const TimelineFrameThumbnails: React.FC<TimelineFrameThumbnailsPropsWithFallback> =
  observer(
    ({
      storyId,
      videoId,
      trimStart,
      duration,
      elementWidth,
      height,
      videoWidth,
      videoHeight,
      fallbackThumbnailUrl,
    }) => {
      const videoCreator = useVideoCreatorStore();
      const videoCacheKey = getVideoCacheKey(storyId, videoId);
      const [, forceUpdate] = useState({});
      const [shouldUseFallback, setShouldUseFallback] = useState(
        videosWithoutFrames.has(videoCacheKey),
      );
      const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
      const [retryCount, setRetryCount] = useState(0);
      const retryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
        null,
      );

      // Generate unique element ID for tracking loading state
      const elementIdRef = useRef(
        `thumbnail-${storyId}-${videoId}-${Math.random()
          .toString(36)
          .substring(2, 11)}`,
      );
      const elementId = elementIdRef.current;

      // Get or create cache for this video
      const getVideoCache = useCallback(() => {
        if (!globalFrameCache.has(videoCacheKey)) {
          globalFrameCache.set(videoCacheKey, new Map<number, string>());
        }
        return globalFrameCache.get(videoCacheKey)!;
      }, [videoCacheKey]);

      // Handle frames loaded from worker
      const handleFramesLoaded = useCallback(
        (frames: Array<{ frameNumber: number; url: string }>) => {
          const videoCache = getVideoCache();
          let hasNewFrames = false;

          frames.forEach(frame => {
            const previous = videoCache.get(frame.frameNumber);
            // A fetchable pipeline frame is authoritative and must replace a
            // session-local poster. Conversely, never let a late local seek
            // overwrite a server frame that already landed.
            if (
              previous !== frame.url &&
              !(
                frame.url.startsWith('blob:') &&
                previous &&
                !previous.startsWith('blob:')
              )
            ) {
              if (previous?.startsWith('blob:')) {
                URL.revokeObjectURL(previous);
              }
              videoCache.set(frame.frameNumber, frame.url);
              hasNewFrames = true;
            }
          });

          // Only force re-render if we got new frames
          if (hasNewFrames) {
            // Clear processing state since we successfully loaded frames
            videosWithProcessingImages.delete(videoCacheKey);
            forceUpdate({});
            // Clear retry interval on successful load
            if (retryIntervalRef.current) {
              clearInterval(retryIntervalRef.current);
              retryIntervalRef.current = null;
              setRetryCount(0);
            }
          }
        },
        [getVideoCache, videoCacheKey],
      );

      // Handle load errors - fallback to legacy behavior
      const handleLoadError = useCallback(() => {
        videosWithoutFrames.add(videoCacheKey);
        setShouldUseFallback(true);
        // Clear any retry interval when we definitively fail
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
      }, [videoCacheKey]);

      // Handle images that are still processing (403 errors)
      const handleImagesProcessing = useCallback(() => {
        const hasLocalFrames = Array.from(getVideoCache().values()).some(url =>
          url.startsWith('blob:'),
        );
        if (hasLocalFrames) {
          videosWithProcessingImages.delete(videoCacheKey);
        } else {
          videosWithProcessingImages.add(videoCacheKey);
        }
        forceUpdate({}); // Force re-render to show processing state
      }, [getVideoCache, videoCacheKey]);

      // Use the frame loader hook
      const { loadFrames, isLoading: isFrameLoading } = useFrameLoader({
        apiUrl: process.env.REACT_APP_API_URL || '',
        onFramesLoaded: handleFramesLoaded,
        onImagesProcessing: handleImagesProcessing,
      });
      const frameLoadingRef = useRef(isFrameLoading);
      useEffect(() => {
        frameLoadingRef.current = isFrameLoading;
      }, [isFrameLoading]);
      const serverProbeInFlightRef = useRef(false);

      // PLAT-11228: the session may still hold the uploaded file — frames
      // can be cut from it locally while the pipeline's are still processing.
      const localPreviewUrl = useLocalFramePreviewVideoUrl(storyId);
      const localExtractInFlightRef = useRef(false);
      const renditionPending =
        !!videoCreator.story &&
        isCompressedRenditionPending(videoCreator.story);

      // Check thumbnail loading state based on story data
      const thumbnailLoadingState = getThumbnailLoadingState(
        videoCreator.story,
        videoCacheKey,
        hasAttemptedLoad,
        isFrameLoading,
      );

      const isTimelineReady = videoCreator.isVideoCreatorReady;

      // Calculate which frames to display
      const frameData = useMemo(() => {
        if (!isTimelineReady) {
          return [];
        }

        // Calculate optimal frame width based on aspect ratio
        let optimalFrameWidth: number;
        if (videoWidth && videoHeight && videoHeight > 0) {
          // Calculate width maintaining aspect ratio
          const aspectRatio = videoWidth / videoHeight;
          optimalFrameWidth = Math.round(height * aspectRatio);
        } else {
          // Fallback to target width if dimensions not available
          optimalFrameWidth = TARGET_FRAME_WIDTH;
        }

        // Clamp frame width to reasonable bounds for visual quality
        const frameWidth = Math.max(
          MIN_FRAME_WIDTH,
          Math.min(MAX_FRAME_WIDTH, optimalFrameWidth),
        );

        // Calculate frame count based on available space and target density
        let frameCount = Math.max(1, Math.floor(elementWidth / frameWidth));

        // Limit frame count to available duration (since frames are 1 per second)
        // We can't show more frames than we have seconds of video
        // Ensure at least 1 frame if duration > 0
        const maxAvailableFrames = Math.max(1, Math.floor(duration));
        frameCount = Math.min(frameCount, maxAvailableFrames);

        // Apply performance limits
        if (frameCount > FALLBACK_THRESHOLD) {
          // For extremely long videos, return empty array to trigger fallback
          return [];
        }

        // Apply absolute maximum for performance, but scale with element size
        const dynamicMaxFrames = Math.min(
          ABSOLUTE_MAX_FRAMES,
          Math.floor(elementWidth / MIN_FRAME_WIDTH), // Allow more frames for wider elements
        );

        if (frameCount > dynamicMaxFrames) {
          frameCount = dynamicMaxFrames;
        }

        // Calculate frame interval based on final frame count
        const frameInterval = duration / frameCount;

        // Generate array of frame numbers (1-based as files are 000001.jpg, 000002.jpg, etc.)
        const frames = Array.from({ length: frameCount }, (_, i) => {
          // Calculate time for this frame slot in seconds
          const time = trimStart + i * frameInterval + frameInterval / 2;
          // Since frames are saved one per second, frame number = seconds (rounded)
          // Ensure we get unique frame numbers by using the frame index when interval is small
          let frameNumber: number;
          if (frameCount === 1) {
            // Single frame: use the middle of the duration
            frameNumber = Math.max(1, Math.round(trimStart + duration / 2));
          } else if (frameInterval < 1) {
            // Multiple frames but interval < 1: distribute evenly, avoiding rounding collisions
            // Use floor for earlier frames, ceil for later frames to maximize spread
            const progress = i / (frameCount - 1);
            const exactFrame = trimStart + progress * duration;
            // Alternate between floor and ceil to avoid all frames rounding to the same value
            frameNumber = Math.max(
              1,
              i % 2 === 0 ? Math.floor(exactFrame) : Math.ceil(exactFrame),
            );
          } else {
            frameNumber = Math.max(1, Math.round(time));
          }

          return {
            index: i,
            frameNumber,
            time,
            width: elementWidth / frameCount,
          };
        });

        return frames;
      }, [
        isTimelineReady,
        trimStart,
        duration,
        elementWidth,
        height,
        videoWidth,
        videoHeight,
      ]);

      // Auto-retry polling for processing thumbnails
      useEffect(() => {
        if (
          DISABLE_FRAME_LOADING ||
          shouldUseFallback ||
          thumbnailLoadingState.state !== 'processing'
        ) {
          // Clear any existing interval if not in processing state
          if (retryIntervalRef.current) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
          }
          return;
        }

        // Set up polling interval for processing thumbnails
        if (!retryIntervalRef.current) {
          const retryInterval = 60000; // 60s

          retryIntervalRef.current = setInterval(() => {
            const videoCache = getVideoCache();
            const uncachedFrames = frameData
              .map(f => f.frameNumber)
              .filter(num => {
                const url = videoCache.get(num);
                return !url || url.startsWith('blob:');
              });

            if (uncachedFrames.length > 0) {
              setRetryCount(prev => prev + 1);
              setHasAttemptedLoad(true);
              // Clear processing state before retry - it will be re-added if still processing
              videosWithProcessingImages.delete(videoCacheKey);
              loadFrames(storyId, videoId, uncachedFrames, handleLoadError);
            } else {
              // All frames are cached, clear the interval and processing state
              videosWithProcessingImages.delete(videoCacheKey);
              if (retryIntervalRef.current) {
                clearInterval(retryIntervalRef.current);
                retryIntervalRef.current = null;
                setRetryCount(0);
              }
            }
          }, retryInterval);
        }

        // Cleanup on unmount or state change
        return () => {
          if (retryIntervalRef.current) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
          }
        };
      }, [
        thumbnailLoadingState.state,
        shouldUseFallback,
        retryCount,
        videoCacheKey,
        frameData,
        getVideoCache,
        loadFrames,
        storyId,
        videoId,
        handleLoadError,
      ]);

      // PLAT-11228: while renditions are pending, the pipeline's per-second
      // thumbnails cannot exist yet (they are cut from the 270p at the very
      // end) — the worker still hands back their URLs, which land in the cache
      // as broken images. If this session holds the uploaded file, cut the
      // visible frames from it locally and let them win over those
      // placeholders; the filmstrip fills in seconds instead of at the end.
      // Strictly additive: no file, undecodable file, or any failure leaves
      // the existing behaviour exactly as it was.
      useEffect(() => {
        if (
          DISABLE_FRAME_LOADING ||
          shouldUseFallback ||
          !renditionPending ||
          !localPreviewUrl ||
          localExtractInFlightRef.current
        ) {
          return;
        }
        const videoCache = getVideoCache();
        const needed = [
          ...new Set(
            frameData
              .map(f => f.frameNumber)
              .filter(num => !videoCache.has(num)),
          ),
        ];
        if (needed.length === 0) {
          // Everything visible is already served locally — a late "still
          // processing" report from the worker must not veil it.
          if (videosWithProcessingImages.has(videoCacheKey)) {
            videosWithProcessingImages.delete(videoCacheKey);
            forceUpdate({});
          }
          return;
        }

        const controller = new AbortController();
        localExtractInFlightRef.current = true;
        void extractLocalFrameThumbnails(
          localPreviewUrl,
          needed,
          height,
          controller.signal,
        ).then(frames => {
          localExtractInFlightRef.current = false;
          if (controller.signal.aborted || frames.length === 0) return;
          frames.forEach(frame => videoCache.set(frame.frameNumber, frame.url));
          videosWithProcessingImages.delete(videoCacheKey);
          forceUpdate({});
        });
        return () => {
          controller.abort();
          localExtractInFlightRef.current = false;
        };
      }, [
        renditionPending,
        shouldUseFallback,
        localPreviewUrl,
        frameData,
        height,
        getVideoCache,
        videoCacheKey,
      ]);

      // The 270p readiness signal can precede thumbnail extraction by a few
      // seconds. Once video processing flips ready, immediately request every
      // missing/local frame and keep retrying until each one is a server URL.
      // Local blobs stay visible during 403s and are replaced atomically on
      // success by handleFramesLoaded.
      useEffect(() => {
        if (
          DISABLE_FRAME_LOADING ||
          shouldUseFallback ||
          renditionPending ||
          frameData.length === 0
        ) {
          return;
        }

        const requestServerFrames = () => {
          if (frameLoadingRef.current || serverProbeInFlightRef.current) {
            return false;
          }
          const videoCache = getVideoCache();
          const pending = [
            ...new Set(
              frameData
                .map(frame => frame.frameNumber)
                .filter(frameNumber => {
                  const url = videoCache.get(frameNumber);
                  return !url || url.startsWith('blob:');
                }),
            ),
          ];
          if (pending.length === 0) return true;
          setHasAttemptedLoad(true);
          serverProbeInFlightRef.current = true;
          loadFrames(storyId, videoId, pending, handleLoadError, () => {
            serverProbeInFlightRef.current = false;
          });
          return false;
        };

        const timer = setInterval(() => {
          if (requestServerFrames()) clearInterval(timer);
        }, SERVER_FRAME_RETRY_MS);
        return () => clearInterval(timer);
      }, [
        renditionPending,
        shouldUseFallback,
        frameData,
        getVideoCache,
        loadFrames,
        storyId,
        videoId,
        handleLoadError,
      ]);

      // Handle fallback when frame count exceeds threshold
      useEffect(() => {
        if (frameData.length === 0 && !shouldUseFallback) {
          setShouldUseFallback(true);
        } else if (
          frameData.length > 0 &&
          shouldUseFallback &&
          !videosWithoutFrames.has(videoCacheKey)
        ) {
          // Reset fallback if we now have frameData and haven't previously failed to load frames
          setShouldUseFallback(false);
        }
      }, [frameData.length, shouldUseFallback, videoCacheKey]);

      // Load frames when needed
      useEffect(() => {
        // Skip if disabled, already fell back to legacy, or thumbnails not available
        if (
          DISABLE_FRAME_LOADING ||
          shouldUseFallback ||
          thumbnailLoadingState.state !== 'available'
        ) {
          return;
        }

        const videoCache = getVideoCache();

        // Get frame numbers that aren't cached
        const uncachedFrames = frameData
          .map(f => f.frameNumber)
          .filter(num => {
            const url = videoCache.get(num);
            return !url || (!renditionPending && url.startsWith('blob:'));
          });

        // Remove duplicates
        const uniqueUncachedFrames = [...new Set(uncachedFrames)];

        if (uniqueUncachedFrames.length > 0) {
          setHasAttemptedLoad(true);
          loadFrames(storyId, videoId, uniqueUncachedFrames, handleLoadError);
        }
      }, [
        storyId,
        videoId,
        frameData,
        getVideoCache,
        loadFrames,
        shouldUseFallback,
        handleLoadError,
        thumbnailLoadingState.state,
        renditionPending,
      ]);

      // Track loading state with VideoCreatorStore
      useEffect(() => {
        if (
          DISABLE_FRAME_LOADING ||
          shouldUseFallback ||
          thumbnailLoadingState.state !== 'available'
        ) {
          videoCreator.unregisterThumbnailLoading(elementId);
          return;
        }

        if (isFrameLoading && !videoCreator.isVideoCreatorReady) {
          videoCreator.registerThumbnailLoading(elementId);
        } else {
          videoCreator.unregisterThumbnailLoading(elementId);
        }

        // Cleanup on unmount
        return () => {
          videoCreator.unregisterThumbnailLoading(elementId);
        };
      }, [
        isFrameLoading,
        elementId,
        videoCreator,
        shouldUseFallback,
        thumbnailLoadingState.state,
      ]);

      // Function to get the best available frame URL from cache
      // Maximum distance (in seconds) to allow for fallback frames
      // Don't show a frame that's more than 10 seconds away from the target
      const MAX_FRAME_DISTANCE = 10;

      const getBestAvailableFrame = useCallback(
        (
          targetFrame: number,
        ): { url: string | null; frameNumber: number; isExact: boolean } => {
          const videoCache = getVideoCache();

          // First, try to get the exact frame
          if (videoCache.has(targetFrame)) {
            return {
              url: videoCache.get(targetFrame)!,
              frameNumber: targetFrame,
              isExact: true,
            };
          }

          // If not available, find the closest cached frame
          const cachedFrameNumbers = Array.from(videoCache.keys()).sort(
            (a, b) => a - b,
          );
          if (cachedFrameNumbers.length === 0) {
            return { url: null, frameNumber: targetFrame, isExact: false };
          }

          // Binary search for closest frame
          let left = 0;
          let right = cachedFrameNumbers.length - 1;
          let closest = cachedFrameNumbers[0];
          let minDiff = Math.abs(targetFrame - closest);

          while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const midFrame = cachedFrameNumbers[mid];
            const diff = Math.abs(targetFrame - midFrame);

            if (diff < minDiff) {
              minDiff = diff;
              closest = midFrame;
            }

            if (midFrame < targetFrame) {
              left = mid + 1;
            } else if (midFrame > targetFrame) {
              right = mid - 1;
            } else {
              break;
            }
          }

          // Don't use a frame that's too far from the target
          if (minDiff > MAX_FRAME_DISTANCE) {
            return { url: null, frameNumber: targetFrame, isExact: false };
          }

          return {
            url: videoCache.get(closest)!,
            frameNumber: closest,
            isExact: false,
          };
        },
        [getVideoCache],
      );

      // Render legacy single thumbnail if fallback is needed
      if (shouldUseFallback && fallbackThumbnailUrl) {
        return (
          <Container
            style={{
              height,
              backgroundImage: `url("${fallbackThumbnailUrl}")`,
              backgroundRepeat: 'repeat-x',
              backgroundSize: 'auto 100%',
            }}
          />
        );
      }

      return (
        <Container style={{ height }}>
          {DISABLE_FRAME_LOADING ? (
            // Show simple placeholder when loading is disabled
            <PlaceholderFrame style={{ width: '100%', height }}>
              Frame loading disabled
            </PlaceholderFrame>
          ) : thumbnailLoadingState.state === 'unavailable' ||
            thumbnailLoadingState.state === 'failed' ? (
            // Show fallback thumbnail for unavailable or failed states
            fallbackThumbnailUrl ? (
              <Container
                style={{
                  height,
                  backgroundImage: `url("${fallbackThumbnailUrl}")`,
                  backgroundRepeat: 'repeat-x',
                  backgroundSize: 'auto 100%',
                }}
              />
            ) : (
              <>
                <PlaceholderFrame style={{ width: '100%', height }} />
                <LoadingOverlay>
                  <LoadingMessage>
                    {thumbnailLoadingState.message}
                  </LoadingMessage>
                </LoadingOverlay>
              </>
            )
          ) : thumbnailLoadingState.state === 'processing' ? (
            // Show loading overlay for processing thumbnails
            <>
              <PlaceholderFrame style={{ width: '100%', height }} />
              <LoadingOverlay>
                <SimpleLoadingSpinner
                  spinnerSize={24}
                  spinnerWidth={3}
                  icon={<PlayIconRect width="8" height="8" />}
                  text={null}
                />
                <LoadingMessage>{thumbnailLoadingState.message}</LoadingMessage>
              </LoadingOverlay>
            </>
          ) : (
            frameData.map(({ index, frameNumber, time, width }) => {
              const { url: frameUrl, isExact } =
                getBestAvailableFrame(frameNumber);

              if (frameUrl) {
                return (
                  <FrameImage
                    key={`${index}-${frameNumber}`}
                    src={frameUrl}
                    style={{ width }}
                    isLoading={!isExact}
                    alt={`Frame at ${time.toFixed(1)}s`}
                    loading="lazy"
                    decoding="async"
                  />
                );
              }

              // Show placeholder while loading
              return (
                <PlaceholderFrame
                  key={`${index}-${frameNumber}`}
                  style={{ width, height }}
                ></PlaceholderFrame>
              );
            })
          )}
        </Container>
      );
    },
  );

TimelineFrameThumbnails.displayName = 'TimelineFrameThumbnails';
